
import { create } from 'zustand';
import apiClient from '@/services/apiClient';
import { API_ENDPOINTS } from '@/config/apiEndpoints';

// Types matching backend structure
export interface ChatParticipant {
    id: string;
    full_name: string;
    username?: string;
    avatar_url?: string;
    role?: 'admin' | 'member';
    joined_at?: string;
}

export interface ChatRoom {
    room_id: string;
    room_type: 'customer_customer' | 'business_specific' | 'property_specific' | 'property_tenant';
    room_name: string;
    room_avatar_url?: string;
    is_group: boolean;
    group_name?: string;
    group_description?: string;
    group_avatar_url?: string;
    type: string;
    created_by?: string;
    business_id?: string;
    business_name?: string;
    business_logo?: string;
    property_id?: string;
    property_name?: string;
    property_image?: string;
    unit_number?: string;
    participants: ChatParticipant[];
    last_message_content?: string;
    last_message_created_at?: string;
    unread_count: number;
    is_new_connection: boolean;
    allow_calls: boolean;
    allow_group_calls: boolean;
    max_call_participants: number;
    members_can_add?: boolean;
    members_can_edit?: boolean;
    invite_link_enabled?: boolean;
    created_at: string;
}

export interface Message {
    id: string;
    room_id: string;
    sender_id: string;
    content: string;
    media_url?: string;
    media_type?: 'image' | 'video' | 'audio' | 'document';
    media_size?: number;
    media_duration?: number;
    thumbnail_key?: string;
    is_system_message?: boolean;
    is_deleted?: boolean;
    created_at: string;
    updated_at?: string;
    profile?: {
        id: string;
        full_name: string;
        avatar_url?: string;
    };
}

export interface MessageRequest {
    id: string;
    sender_id: string;
    recipient_id: string;
    message: string;
    status: 'pending' | 'accepted' | 'declined';
    created_at: string;
    sender?: {
        id: string;
        full_name: string;
        username?: string;
        avatar_url?: string;
    };
}

export interface Business {
    id: string;
    name: string;
    logo_url?: string;
    description?: string;
}

export interface MediaItem {
    id: string;
    content?: string;
    media_url: string;
    media_type: string;
    media_size?: number;
    media_duration?: number;
    thumbnail_key?: string;
    created_at: string;
    sender_id: string;
    profile?: {
        id: string;
        full_name: string;
        avatar_url?: string;
    };
}

export interface GroupMember {
    user_id: string;
    role: 'admin' | 'member';
    joined_at: string;
    profile: {
        id: string;
        username?: string;
        full_name: string;
        avatar_url?: string;
    };
}

export interface GroupDetails {
    id: string;
    group_name: string;
    group_description?: string;
    group_avatar_url?: string;
    business_id?: string;
    property_id?: string;
    created_by: string;
    created_at: string;
    is_group: boolean;
    member_count: number;
    members_can_add?: boolean;
    members_can_edit?: boolean;
    invite_link_enabled?: boolean;
}

export interface GroupInviteInfo {
    invite_code: string;
    invite_link: string;
    deep_link: string;
    created_at: string;
    use_count: number;
}

export interface GroupPreview {
    id: string;
    group_name: string;
    group_description?: string;
    group_avatar_url?: string;
    member_count: number;
}

export interface UploadSession {
    sessionId: string;
    uploadKey: string;
    chunksTotal: number;
    chunksUploaded: number;
}

export interface UploadResult {
    mediaKey: string;
    mediaUrl: string;
    thumbnailKey?: string;
    size: number;
    duration?: number;
}

export interface PotentialMember {
    id: string;
    username?: string;
    full_name: string;
    avatar_url?: string;
}

interface ChatState {
    // Data
    chatRooms: ChatRoom[];
    activeRoom: ChatRoom | null;
    messages: Message[];
    messageRequests: MessageRequest[];
    userBusinesses: Business[];
    groupMembers: GroupMember[];
    groupMedia: MediaItem[];
    groupDetails: GroupDetails | null;
    potentialMembers: PotentialMember[];
    inviteLink: GroupInviteInfo | null;
    groupPreview: GroupPreview | null;
    uploadSession: UploadSession | null;

    // Loading states
    loading: boolean;
    roomsLoading: boolean;
    messagesLoading: boolean;
    requestsLoading: boolean;
    businessesLoading: boolean;
    sendingMessage: boolean;
    uploadingMedia: boolean;
    creatingGroup: boolean;
    groupDetailsLoading: boolean;
    groupMembersLoading: boolean;
    groupMediaLoading: boolean;
    leavingGroup: boolean;
    updatingGroup: boolean;
    addingMembers: boolean;
    updatingRole: boolean;
    removingMember: boolean;
    generatingInvite: boolean;
    joiningGroup: boolean;
    revokingInvite: boolean;
    uploadingAvatar: boolean;

    // Error states
    error: string | null;
    roomsError: string | null;
    messagesError: string | null;
    requestsError: string | null;
    businessesError: string | null;
    sendMessageError: string | null;
    uploadError: string | null;
    groupError: string | null;
    groupDetailsError: string | null;
    groupMembersError: string | null;
    groupMediaError: string | null;
    leaveGroupError: string | null;
    updateGroupError: string | null;
    addMembersError: string | null;
    updateRoleError: string | null;
    removeMemberError: string | null;
    inviteError: string | null;
    joinGroupError: string | null;
    revokeInviteError: string | null;
    uploadAvatarError: string | null;

    // Pagination
    messagesPage: number;
    messagesHasMore: boolean;
    mediaPage: number;
    mediaHasMore: boolean;

    // Actions
    // Chat Rooms
    fetchChatRooms: (businessId?: string, propertyId?: string) => Promise<void>;
    setActiveRoom: (room: ChatRoom | null) => void;
    createDirectRoom: (
        recipientId?: string,
        businessId?: string,
        propertyId?: string
    ) => Promise<ChatRoom>;

    // Messages
    fetchMessages: (roomId: string, page?: number) => Promise<void>;
    sendMessage: (roomId: string, content: string) => Promise<void>;
    sendMediaMessage: (roomId: string, file: File) => Promise<UploadResult | void>;
    loadMoreMessages: () => Promise<void>;

    // Message Requests
    fetchMessageRequests: () => Promise<void>;
    sendMessageRequest: (recipientId: string, message: string) => Promise<void>;
    respondToMessageRequest: (requestId: string, action: 'accept' | 'decline') => Promise<void>;

    // Businesses
    fetchUserBusinesses: () => Promise<void>;

    // Media Upload
    uploadMedia: (roomId: string, file: File) => Promise<UploadResult>;
    initChunkedUpload: (
        filename: string,
        fileSize: number,
        mimeType: string,
        roomId: string
    ) => Promise<UploadSession>;
    uploadChunk: (sessionId: string, chunkIndex: number, chunk: Blob) => Promise<{
        chunkIndex: number;
        chunksUploaded: number;
        chunksTotal: number;
    }>;
    completeChunkedUpload: (sessionId: string) => Promise<UploadResult>;
    abortChunkedUpload: (sessionId: string) => Promise<void>;

    // Group Management
    createGroup: (
        name: string,
        memberIds: string[],
        context: 'personal' | 'business' | 'property',
        businessId?: string,
        propertyId?: string,
        description?: string
    ) => Promise<ChatRoom>;
    fetchPotentialMembers: (
        context: 'personal' | 'business' | 'property',
        businessId?: string,
        propertyId?: string
    ) => Promise<void>;

    // Group Details
    fetchGroupDetails: (roomId: string) => Promise<void>;
    fetchGroupMembers: (roomId: string) => Promise<void>;
    fetchGroupMedia: (
        roomId: string,
        type?: 'image' | 'video' | 'audio' | 'document',
        page?: number
    ) => Promise<void>;
    leaveGroup: (roomId: string) => Promise<void>;

    // Group Editing
    updateGroup: (
        roomId: string,
        updates: {
            group_name?: string;
            group_description?: string;
            group_avatar_url?: string;
            members_can_add?: boolean;
            members_can_edit?: boolean;
            invite_link_enabled?: boolean;
        }
    ) => Promise<void>;
    uploadGroupAvatar: (roomId: string, file: File) => Promise<string>;
    addGroupMembers: (roomId: string, memberIds: string[]) => Promise<void>;
    updateMemberRole: (roomId: string, memberId: string, role: 'admin' | 'member') => Promise<void>;
    removeGroupMember: (roomId: string, memberId: string) => Promise<void>;

    // Group Invites
    getOrCreateInviteLink: (roomId: string) => Promise<GroupInviteInfo>;
    getGroupByInviteCode: (inviteCode: string) => Promise<GroupPreview>;
    joinGroupViaInvite: (inviteCode: string) => Promise<string>;
    revokeInviteLink: (roomId: string) => Promise<void>;

    // Utility
    markMessagesAsRead: (roomId: string) => Promise<void>;
    clearErrors: () => void;
    resetMessages: () => void;
    resetGroupState: () => void;
}

// Get current user ID from Supabase session
const getCurrentUserId = () => {
    try {
        const raw = localStorage.getItem('majengo-auth');
        if (raw) {
            const parsed = JSON.parse(raw);
            return parsed?.user?.id || parsed?.session?.user?.id || null;
        }
    } catch { /* ignore */ }
    return null;
};

export const useChatScreen = create<ChatState>((set, get) => ({
    // Initial state
    chatRooms: [],
    activeRoom: null,
    messages: [],
    messageRequests: [],
    userBusinesses: [],
    groupMembers: [],
    groupMedia: [],
    groupDetails: null,
    potentialMembers: [],
    inviteLink: null,
    groupPreview: null,
    uploadSession: null,

    loading: false,
    roomsLoading: false,
    messagesLoading: false,
    requestsLoading: false,
    businessesLoading: false,
    sendingMessage: false,
    uploadingMedia: false,
    creatingGroup: false,
    groupDetailsLoading: false,
    groupMembersLoading: false,
    groupMediaLoading: false,
    leavingGroup: false,
    updatingGroup: false,
    addingMembers: false,
    updatingRole: false,
    removingMember: false,
    generatingInvite: false,
    joiningGroup: false,
    revokingInvite: false,
    uploadingAvatar: false,

    error: null,
    roomsError: null,
    messagesError: null,
    requestsError: null,
    businessesError: null,
    sendMessageError: null,
    uploadError: null,
    groupError: null,
    groupDetailsError: null,
    groupMembersError: null,
    groupMediaError: null,
    leaveGroupError: null,
    updateGroupError: null,
    addMembersError: null,
    updateRoleError: null,
    removeMemberError: null,
    inviteError: null,
    joinGroupError: null,
    revokeInviteError: null,
    uploadAvatarError: null,

    messagesPage: 1,
    messagesHasMore: false,
    mediaPage: 1,
    mediaHasMore: false,

    // ========== Chat Rooms ==========
    fetchChatRooms: async (businessId?: string, propertyId?: string) => {
        set({ roomsLoading: true, roomsError: null });

        try {
            const params: Record<string, string> = {};
            if (businessId) params.businessId = businessId;
            if (propertyId) params.propertyId = propertyId;

            const response = await apiClient.get(API_ENDPOINTS.CHAT_ROOMS, { params })
                .catch(error => {
                    if (error.response?.status === 401) {
                        console.warn('[ChatStore] Unauthorized - user not logged in for chat');
                        return { data: [] }; // Return empty array for unauthorized
                    }
                    throw error;
                });

            set({
                chatRooms: response.data || [],
                roomsLoading: false
            });
        } catch (error: any) {
            console.error('Failed to fetch chat rooms:', error);
            set({
                roomsLoading: false,
                roomsError: error.response?.status === 401
                    ? 'Please login to view chats'
                    : 'Chats temporarily unavailable',
                chatRooms: [],
            });
        }
    },

    setActiveRoom: (room) => {
        set({ activeRoom: room });
        if (room) {
            get().markMessagesAsRead(room.room_id);
        } else {
            get().resetMessages();
        }
    },

    createDirectRoom: async (recipientId?: string, businessId?: string, propertyId?: string) => {
        set({ loading: true, error: null });

        try {
            const payload: any = {};
            if (recipientId) payload.recipientId = recipientId;
            if (businessId) payload.businessId = businessId;
            if (propertyId) payload.propertyId = propertyId;

            const response = await apiClient.post(API_ENDPOINTS.CHAT_DIRECT, payload);
            const newRoom = response.data;

            // Add to chat rooms list if not already present
            set((state) => {
                const exists = state.chatRooms.some(room => room.room_id === newRoom.room_id);
                return {
                    chatRooms: exists ? state.chatRooms : [newRoom, ...state.chatRooms],
                    activeRoom: newRoom,
                    loading: false,
                };
            });

            return newRoom;
        } catch (error: any) {
            console.error('Failed to create direct room:', error);
            set({ loading: false, error: 'Failed to create chat' });
            throw error;
        }
    },

    // ========== Messages ==========
    // ========== Messages ==========
    fetchMessages: async (roomId: string, page = 1) => {
        set({ messagesLoading: true, messagesError: null });

        try {
            const params: Record<string, any> = { limit: 50 };

            // Use cursor-based pagination for older messages (page > 1)
            if (page > 1) {
                const existing = get().messages;
                if (existing.length > 0) {
                    // messages are stored oldest-first; oldest is at index 0
                    params.before = existing[0].created_at;
                }
            }

            const response = await apiClient.get(`/messages/${roomId}`, { params });

            // Backend returns newest-first — reverse so oldest is at top
            const ordered: Message[] = [...(response.data || [])].reverse();

            set({
                // Prepend older messages when paginating, replace on first load
                messages: page === 1 ? ordered : [...ordered, ...get().messages],
                messagesPage: page,
                messagesHasMore: response.data.length === 50,
                messagesLoading: false,
            });
        } catch (error: any) {
            console.error('Failed to fetch messages:', error);
            set({
                messagesLoading: false,
                messagesError: error.response?.status === 401
                    ? 'Please login to view messages'
                    : 'Messages temporarily unavailable',
                messages: []
            });
        }
    },


    sendMessage: async (roomId: string, content: string) => {
        const state = get();
        if (state.sendingMessage || !content.trim()) return;

        set({ sendingMessage: true, sendMessageError: null });

        try {
            // CORRECT: Send to /api/messages with room_id in body
            const tempId = `temp-${Date.now()}`;
            const currentUserId = getCurrentUserId();

            const tempMessage: Message = {
                id: tempId,
                room_id: roomId,
                sender_id: currentUserId,
                content: content.trim(),
                created_at: new Date().toISOString(),
                profile: {
                    id: currentUserId,
                    full_name: 'You',
                }
            };

            // Optimistic update — append to end (messages stored oldest-first)
            set((state) => ({
                messages: [...state.messages, tempMessage],
            }));

            const response = await apiClient.post(
                API_ENDPOINTS.CHAT_SEND_MESSAGE,
                { room_id: roomId, content: content.trim() }
            );

            // Replace temp with confirmed message
            set((state) => ({
                messages: state.messages.map(msg =>
                    msg.id === tempId ? response.data : msg
                ),
                sendingMessage: false,
            }));

            // Update the last message in the chat room
            set((state) => ({
                chatRooms: state.chatRooms.map(room =>
                    room.room_id === roomId
                        ? {
                            ...room,
                            last_message_content: content.trim(),
                            last_message_created_at: new Date().toISOString(),
                            unread_count: 0
                        }
                        : room
                )
            }));

        } catch (error: any) {
            console.error('Failed to send message:', error);
            // Remove temp message on error
            set((state) => ({
                messages: state.messages.filter(msg => !msg.id.startsWith('temp-')),
                sendingMessage: false,
                sendMessageError: error.response?.data?.error || 'Failed to send message',
            }));
        }
    },

    sendMediaMessage: async (roomId: string, file: File) => {
        set({ uploadingMedia: true, uploadError: null });

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('roomId', roomId);

            let uploadResult: UploadResult;

            try {
                const response = await apiClient.post(API_ENDPOINTS.CHAT_UPLOAD_MEDIA, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                uploadResult = response.data;
            } catch (error: any) {
                console.warn('Media upload failed, using mock data for development:', error);
                // Return mock data for development
                uploadResult = {
                    mediaKey: `dev-${Date.now()}`,
                    mediaUrl: URL.createObjectURL(file),
                    size: file.size,
                    thumbnailKey: file.type.startsWith('video/') ? `thumb-${Date.now()}` : undefined,
                };
            }

            // Send message with media info
            const messageContent = file.type.startsWith('image/')
                ? '📷 Photo'
                : file.type.startsWith('video/')
                    ? '🎥 Video'
                    : '📎 File';

            await get().sendMessage(roomId, `${messageContent}: ${file.name}`);

            set({ uploadingMedia: false });
            return uploadResult;
        } catch (error: any) {
            console.error('Failed to send media message:', error);
            set({ uploadingMedia: false, uploadError: 'Failed to upload media' });
            throw error;
        }
    },

    loadMoreMessages: async () => {
        const state = get();
        if (!state.activeRoom || !state.messagesHasMore || state.messagesLoading) return;

        const nextPage = state.messagesPage + 1;
        await get().fetchMessages(state.activeRoom.room_id, nextPage);
    },

    // ========== Message Requests ==========
    fetchMessageRequests: async () => {
        set({ requestsLoading: true, requestsError: null });

        try {
            const response = await apiClient.get(API_ENDPOINTS.CHAT_MESSAGE_REQUESTS)
                .catch(error => {
                    if (error.response?.status === 401) {
                        console.warn('[ChatStore] Unauthorized - user not logged in for message requests');
                        return { data: [] };
                    }
                    throw error;
                });

            set({
                messageRequests: response.data || [],
                requestsLoading: false
            });
        } catch (error: any) {
            console.error('Failed to fetch message requests:', error);
            set({
                requestsLoading: false,
                requestsError: error.response?.status === 401
                    ? 'Please login to view message requests'
                    : 'Message requests temporarily unavailable',
                messageRequests: [],
            });
        }
    },

    sendMessageRequest: async (recipientId: string, message: string) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.CHAT_MESSAGE_REQUESTS, {
                recipientId,
                message,
            });

            // Refresh requests list
            await get().fetchMessageRequests();
            return response.data;
        } catch (error: any) {
            console.error('Failed to send message request:', error);
            throw error;
        }
    },

    respondToMessageRequest: async (requestId: string, action: 'accept' | 'decline') => {
        try {
            const response = await apiClient.put(API_ENDPOINTS.CHAT_REQUEST_RESPONSE(requestId), { action });

            // Refresh requests list
            await get().fetchMessageRequests();
            return response.data;
        } catch (error: any) {
            console.error('Failed to respond to message request:', error);
            throw error;
        }
    },

    // ========== Businesses ==========
    fetchUserBusinesses: async () => {
        set({ businessesLoading: true, businessesError: null });

        try {
            const response = await apiClient.get(API_ENDPOINTS.CHAT_USER_BUSINESSES);
            set({ userBusinesses: response.data || [], businessesLoading: false });
        } catch (error: any) {
            console.error('Failed to fetch user businesses:', error);
            set({
                businessesLoading: false,
                businessesError: 'Businesses temporarily unavailable',
                userBusinesses: [],
            });
        }
    },

    // ========== Media Upload ==========
    uploadMedia: async (roomId: string, file: File) => {
        set({ uploadingMedia: true, uploadError: null });

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('roomId', roomId);

            const response = await apiClient.post(API_ENDPOINTS.CHAT_UPLOAD_MEDIA, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            set({ uploadingMedia: false });
            return response.data;
        } catch (error: any) {
            console.error('Failed to upload media:', error);
            set({ uploadingMedia: false, uploadError: 'Upload failed' });

            // Return mock data for development
            return {
                mediaKey: `dev-${Date.now()}`,
                mediaUrl: URL.createObjectURL(file),
                size: file.size,
                thumbnailKey: file.type.startsWith('video/') ? `thumb-${Date.now()}` : undefined,
            };
        }
    },

    initChunkedUpload: async (filename: string, fileSize: number, mimeType: string, roomId: string) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.CHAT_INIT_CHUNKED_UPLOAD, {
                filename,
                fileSize,
                mimeType,
                roomId,
            });

            set({ uploadSession: response.data });
            return response.data;
        } catch (error: any) {
            console.error('Failed to init chunked upload:', error);
            throw error;
        }
    },

    uploadChunk: async (sessionId: string, chunkIndex: number, chunk: Blob) => {
        try {
            const formData = new FormData();
            formData.append('sessionId', sessionId);
            formData.append('chunkIndex', chunkIndex.toString());
            formData.append('chunk', chunk);

            const response = await apiClient.post(API_ENDPOINTS.CHAT_UPLOAD_CHUNK, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            return response.data;
        } catch (error: any) {
            console.error('Failed to upload chunk:', error);
            throw error;
        }
    },

    completeChunkedUpload: async (sessionId: string) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.CHAT_COMPLETE_CHUNKED_UPLOAD, { sessionId });
            set({ uploadSession: null });
            return response.data;
        } catch (error: any) {
            console.error('Failed to complete upload:', error);
            set({ uploadSession: null });
            throw error;
        }
    },

    abortChunkedUpload: async (sessionId: string) => {
        try {
            await apiClient.post(API_ENDPOINTS.CHAT_ABORT_CHUNKED_UPLOAD, { sessionId });
            set({ uploadSession: null });
        } catch (error: any) {
            console.error('Failed to abort upload:', error);
            throw error;
        }
    },

    // ========== Group Management ==========
    createGroup: async (
        name: string,
        memberIds: string[],
        context: 'personal' | 'business' | 'property',
        businessId?: string,
        propertyId?: string,
        description?: string
    ) => {
        set({ creatingGroup: true, groupError: null });

        try {
            const payload: any = {
                name,
                memberIds,
                context,
            };

            if (description) payload.description = description;
            if (businessId) payload.businessId = businessId;
            if (propertyId) payload.propertyId = propertyId;

            const response = await apiClient.post(API_ENDPOINTS.CHAT_GROUPS, payload);
            const newGroup = response.data;

            // Add to chat rooms list
            set((state) => ({
                chatRooms: [newGroup, ...state.chatRooms],
                creatingGroup: false,
            }));

            return newGroup;
        } catch (error: any) {
            console.error('Failed to create group:', error);
            set({ creatingGroup: false, groupError: 'Failed to create group' });
            throw error;
        }
    },

    fetchPotentialMembers: async (
        context: 'personal' | 'business' | 'property',
        businessId?: string,
        propertyId?: string
    ) => {
        try {
            const params: any = { context };
            if (businessId) params.businessId = businessId;
            if (propertyId) params.propertyId = propertyId;

            const response = await apiClient.get(API_ENDPOINTS.CHAT_POTENTIAL_MEMBERS, { params });
            set({ potentialMembers: response.data?.users || [] });
        } catch (error: any) {
            console.error('Failed to fetch potential members:', error);
            throw error;
        }
    },

    // ========== Group Details ==========
    fetchGroupDetails: async (roomId: string) => {
        set({ groupDetailsLoading: true, groupDetailsError: null });

        try {
            const response = await apiClient.get(API_ENDPOINTS.CHAT_GROUP_DETAILS(roomId));
            set({ groupDetails: response.data, groupDetailsLoading: false });
        } catch (error: any) {
            console.error('Failed to fetch group details:', error);
            set({
                groupDetailsLoading: false,
                groupDetailsError: 'Failed to load group details',
                groupDetails: null,
            });
        }
    },

    fetchGroupMembers: async (roomId: string) => {
        set({ groupMembersLoading: true, groupMembersError: null });

        try {
            const response = await apiClient.get(API_ENDPOINTS.CHAT_GROUP_MEMBERS(roomId));
            set({ groupMembers: response.data?.members || [], groupMembersLoading: false });
        } catch (error: any) {
            console.error('Failed to fetch group members:', error);
            set({
                groupMembersLoading: false,
                groupMembersError: 'Failed to load members',
                groupMembers: [],
            });
        }
    },

    fetchGroupMedia: async (
        roomId: string,
        type?: 'image' | 'video' | 'audio' | 'document',
        page = 1
    ) => {
        set({ groupMediaLoading: true, groupMediaError: null });

        try {
            const params: any = { limit: 50, offset: (page - 1) * 50 };
            if (type) params.type = type;

            const response = await apiClient.get(API_ENDPOINTS.CHAT_GROUP_MEDIA(roomId), { params });
            const mediaData = response.data;

            set((state) => ({
                groupMedia: page === 1 ? mediaData.media : [...state.groupMedia, ...mediaData.media],
                mediaPage: page,
                mediaHasMore: mediaData.has_more,
                groupMediaLoading: false,
            }));
        } catch (error: any) {
            console.error('Failed to fetch group media:', error);
            set({ groupMediaLoading: false, groupMediaError: 'Failed to load media' });
        }
    },

    leaveGroup: async (roomId: string) => {
        set({ leavingGroup: true, leaveGroupError: null });

        try {
            await apiClient.post(API_ENDPOINTS.CHAT_GROUP_LEAVE(roomId));

            // Remove from chat rooms list
            set((state) => ({
                chatRooms: state.chatRooms.filter(room => room.room_id !== roomId),
                activeRoom: state.activeRoom?.room_id === roomId ? null : state.activeRoom,
                leavingGroup: false,
            }));

            // Reset group state
            get().resetGroupState();
        } catch (error: any) {
            console.error('Failed to leave group:', error);
            set({ leavingGroup: false, leaveGroupError: 'Failed to leave group' });
            throw error;
        }
    },

    // ========== Group Editing ==========
    updateGroup: async (roomId: string, updates: any) => {
        set({ updatingGroup: true, updateGroupError: null });

        try {
            const response = await apiClient.patch(API_ENDPOINTS.CHAT_GROUP_UPDATE(roomId), updates);

            // Update in chat rooms list
            set((state) => ({
                chatRooms: state.chatRooms.map(room =>
                    room.room_id === roomId ? { ...room, ...response.data } : room
                ),
                updatingGroup: false,
            }));

            // Update active room if it's the current one
            if (get().activeRoom?.room_id === roomId) {
                set({ activeRoom: { ...get().activeRoom!, ...response.data } });
            }

            // Update group details
            if (get().groupDetails?.id === roomId) {
                set({ groupDetails: { ...get().groupDetails!, ...response.data } });
            }
        } catch (error: any) {
            console.error('Failed to update group:', error);
            set({ updatingGroup: false, updateGroupError: 'Failed to update group' });
            throw error;
        }
    },

    uploadGroupAvatar: async (roomId: string, file: File) => {
        set({ uploadingAvatar: true, uploadAvatarError: null });

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await apiClient.post(API_ENDPOINTS.CHAT_GROUP_AVATAR(roomId), formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            // Update group details
            set((state) => ({
                groupDetails: state.groupDetails ? { ...state.groupDetails, group_avatar_url: response.data.url } : null,
                uploadingAvatar: false,
            }));

            return response.data.url;
        } catch (error: any) {
            console.error('Failed to upload group avatar:', error);
            set({ uploadingAvatar: false, uploadAvatarError: 'Failed to upload avatar' });
            throw error;
        }
    },

    addGroupMembers: async (roomId: string, memberIds: string[]) => {
        set({ addingMembers: true, addMembersError: null });

        try {
            await apiClient.post(API_ENDPOINTS.CHAT_GROUP_ADD_MEMBERS(roomId), { memberIds });

            // Refresh group members
            await get().fetchGroupMembers(roomId);
            set({ addingMembers: false });
        } catch (error: any) {
            console.error('Failed to add members:', error);
            set({ addingMembers: false, addMembersError: 'Failed to add members' });
            throw error;
        }
    },

    updateMemberRole: async (roomId: string, memberId: string, role: 'admin' | 'member') => {
        set({ updatingRole: true, updateRoleError: null });

        try {
            await apiClient.patch(
                API_ENDPOINTS.CHAT_GROUP_UPDATE_MEMBER_ROLE(roomId, memberId),
                { role }
            );

            // Update local state
            set((state) => ({
                groupMembers: state.groupMembers.map(member =>
                    member.user_id === memberId ? { ...member, role } : member
                ),
                updatingRole: false,
            }));
        } catch (error: any) {
            console.error('Failed to update role:', error);
            set({ updatingRole: false, updateRoleError: 'Failed to update role' });
            throw error;
        }
    },

    removeGroupMember: async (roomId: string, memberId: string) => {
        set({ removingMember: true, removeMemberError: null });

        try {
            await apiClient.delete(API_ENDPOINTS.CHAT_GROUP_REMOVE_MEMBER(roomId, memberId));

            // Update local state
            set((state) => ({
                groupMembers: state.groupMembers.filter(member => member.user_id !== memberId),
                removingMember: false,
            }));
        } catch (error: any) {
            console.error('Failed to remove member:', error);
            set({ removingMember: false, removeMemberError: 'Failed to remove member' });
            throw error;
        }
    },

    // ========== Group Invites ==========
    getOrCreateInviteLink: async (roomId: string) => {
        set({ generatingInvite: true, inviteError: null });

        try {
            const response = await apiClient.get(API_ENDPOINTS.CHAT_GROUP_INVITE_LINK(roomId));
            set({ inviteLink: response.data, generatingInvite: false });
            return response.data;
        } catch (error: any) {
            console.error('Failed to get invite link:', error);
            set({ generatingInvite: false, inviteError: 'Failed to get invite link' });
            throw error;
        }
    },

    getGroupByInviteCode: async (inviteCode: string) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.CHAT_GROUP_JOIN_INFO(inviteCode));
            set({ groupPreview: response.data });
            return response.data;
        } catch (error: any) {
            console.error('Failed to get group by invite:', error);
            throw error;
        }
    },

    joinGroupViaInvite: async (inviteCode: string) => {
        set({ joiningGroup: true, joinGroupError: null });

        try {
            const response = await apiClient.post(API_ENDPOINTS.CHAT_GROUP_JOIN(inviteCode));
            const roomId = response.data.room_id;

            // Refresh chat rooms to include the new group
            await get().fetchChatRooms();
            set({ joiningGroup: false });

            return roomId;
        } catch (error: any) {
            console.error('Failed to join group:', error);
            set({ joiningGroup: false, joinGroupError: 'Failed to join group' });
            throw error;
        }
    },

    revokeInviteLink: async (roomId: string) => {
        set({ revokingInvite: true, revokeInviteError: null });

        try {
            await apiClient.delete(API_ENDPOINTS.CHAT_GROUP_REVOKE_INVITE(roomId));
            set({ inviteLink: null, revokingInvite: false });
        } catch (error: any) {
            console.error('Failed to revoke invite:', error);
            set({ revokingInvite: false, revokeInviteError: 'Failed to revoke invite' });
            throw error;
        }
    },

    // ========== Utility ==========
    markMessagesAsRead: async (roomId: string) => {
        // Optimistically clear unread count in UI
        set((state) => ({
            chatRooms: state.chatRooms.map(room =>
                room.room_id === roomId ? { ...room, unread_count: 0 } : room
            ),
        }));
        // Persist read receipt to backend
        try {
            await apiClient.post(`/messages/${roomId}/read`);
        } catch {
            // Non-critical — silently ignore
        }
    },

    clearErrors: () => {
        set({
            error: null,
            roomsError: null,
            messagesError: null,
            requestsError: null,
            businessesError: null,
            sendMessageError: null,
            uploadError: null,
            groupError: null,
            groupDetailsError: null,
            groupMembersError: null,
            groupMediaError: null,
            leaveGroupError: null,
            updateGroupError: null,
            addMembersError: null,
            updateRoleError: null,
            removeMemberError: null,
            inviteError: null,
            joinGroupError: null,
            revokeInviteError: null,
            uploadAvatarError: null,
        });
    },

    resetMessages: () => {
        set({
            messages: [],
            messagesPage: 1,
            messagesHasMore: false,
        });
    },

    resetGroupState: () => {
        set({
            groupMembers: [],
            groupMedia: [],
            groupDetails: null,
            potentialMembers: [],
            inviteLink: null,
            groupPreview: null,
        });
    },
}));